import {resolve} from "../../../container";
import {customElement, property, query} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {TooltipDisplayMode} from "./popoverDisplayMode";
import {ManagingResources} from "../../../common/lifetime";
import {FeatureDetector} from "../../../common/featureDetector";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {Dictionary} from "../dictionary";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement, WithEopSlots} from "../../../common/elements";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {CLOSE_ICON} from "../../../common/svgIcons";

const POPOVER_BACKGROUNDS: Record<string, string> = {
    grayStronger: "popover-color-gray-stronger"
};

@customElement("eop-popover")
export class EopPopover extends WithEopSlots(ManagingResources(UnLitElement)) {

    @query(".popover-container")
    public popoverContainer: HTMLElement;
    @query(".popover")
    public popoverElement: HTMLElement;
    @query(".close-button")
    public popoverCloser: HTMLElement;
    @property({attribute: "enforce-close-button", type: Boolean})
    public enforceCloseButton: boolean = false;
    @property({attribute: "popover-id"})
    public popoverId: string;

    @property({attribute: "background"})
    public background: string;

    private dictionary: Dictionary;
    private displayMode: Maybe<TooltipDisplayMode>;

    public constructor(
        private features: FeatureDetector = resolve(FeatureDetector)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public render(): TemplateResult {
        return html`
            <div class="popover-container">
                <div popover class="popover rich-text">
                    <div class="popover-bubble ${POPOVER_BACKGROUNDS[this.background]}">
                    ${this.renderCloseButton()}
                    ${this.resolveSlot(this.popoverId)}
                   </div>
                </div>
            </div>
        `;
    }

    public renderCloseButton(): Nullable<TemplateResult> {
        if (this.enforceCloseButton || this.features.isTouchDevice()) {
            return html`
                <button type="button"
                        class="close-button"
                        aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                        ${trackingButtonInteract("popover-close-button")}
                        @click=${() => this.close()}
                >${unsafeSVG(CLOSE_ICON)}</button>`;
        } else {
            return null;
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
    }

    public silentlyShowAfter(action: () => void): void {
        this.popoverElement.showPopover();
        action();
    }

    public doHide(): void {
        this.popoverElement.hidePopover();
    }

    public open(anchor: HTMLElement): void {
        this.displayMode = new TooltipDisplayMode(this, anchor, this.popoverElement);
        this.displayMode.open();
    }

    public close(): void {
        this.displayMode?.close();
    }

    public toggle(anchor: HTMLElement): void {
        if (!this.popoverElement.hasAttribute("open")) {
            this.open(anchor);
        } else {
            this.close();
        }
    }
}